import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { useAuth, api } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import VoiceInput from './components/VoiceInput';
import AgeVerificationModal from './components/AgeVerificationModal';
import TermsAndConditions from './pages/TermsAndConditions';
import AdminDashboard from './pages/AdminDashboard';
import TasteProfilePanel from './components/TasteProfilePanel';

// ─── Markdown renderer (bold, italic, bullet lists) ──────────────────────────
function renderMarkdown(text) {
  return text.split('\n').map((line, idx) => {
    // Bullet list items
    const isBullet = /^[-•*]\s/.test(line);
    const content = line.replace(/^[-•*]\s/, '');
    // Parse inline bold (**text**) and italic (*text*)
    const parts = content.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
      if (/^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
      return part;
    });
    if (isBullet) return <li key={idx} style={{ marginLeft: '16px', listStyle: 'disc' }}>{parts}</li>;
    if (!line.trim()) return <br key={idx} />;
    return <p key={idx} style={{ margin: '2px 0' }}>{parts}</p>;
  });
}

// ─── Language configuration ───────────────────────────────────────────────────

const LANGS = [
  { code: 'en', label: 'EN', speechLang: 'en-US', hint: null },
  { code: 'pt', label: 'PT', speechLang: 'pt-BR', hint: '[Please respond in Portuguese.]' },
  { code: 'es', label: 'ES', speechLang: 'es-ES', hint: '[Please respond in Spanish.]' },
];

function detectBrowserLang() {
  const l = (navigator.language || 'en').toLowerCase();
  if (l.startsWith('pt')) return 'pt';
  if (l.startsWith('es')) return 'es';
  return 'en';
}

function getInitialLang() {
  return localStorage.getItem('winesup_lang') || detectBrowserLang();
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

function stripMarkdown(text) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/^[-•]\s/gm, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  const { user, loading, logout, isPremium, effectiveTier, isEffectivePremium, setUser } = useAuth();
  const [messages, setMessages] = useState([
    {
      id: 0,
      role: 'assistant',
      content: 'Hey there! I\'m Vinha, your wine sommelier. Tell me what you\'re eating or what kind of wines you like, and I\'ll help you find the perfect pairing.',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [lang, setLang] = useState(getInitialLang);
  const messagesEndRef = useRef(null);

  // Age verification (for non-authenticated visitors)
  const [ageVerified, setAgeVerified] = useState(
    () => sessionStorage.getItem('age_verified') === '1'
  );

  // Terms acceptance state (checked after login)
  const [termsAccepted, setTermsAccepted] = useState(true); // assume true until we check
  useEffect(() => {
    if (user) {
      setTermsAccepted(!!user.tc_accepted_at);
    }
  }, [user]);

  // ─── Admin dashboard ─────────────────────────────────────────────────────────
  const [showAdmin, setShowAdmin] = useState(false);

  // ─── Taste profile panel ────────────────────────────────────────────────────
  const [showTastePanel, setShowTastePanel] = useState(false);

  // ─── Feature 3: Sommelier complexity level ──────────────────────────────────
  const [sommelierLevel, setSommelierLevel] = useState(() => {
    return localStorage.getItem('winesup_sommelier') || 'balanced';
  });
  const [complexityTooltip, setComplexityTooltip] = useState(false);

  // Sync sommelier level from user profile when user changes
  useEffect(() => {
    if (user && user.sommelier_level) {
      const level = user.sommelier_level;
      // If user has expert stored but is not premium, display balanced without writing back
      if (level === 'expert' && !isEffectivePremium) {
        setSommelierLevel('balanced');
        localStorage.setItem('winesup_sommelier', 'balanced');
      } else {
        setSommelierLevel(level);
        localStorage.setItem('winesup_sommelier', level);
      }
    }
  }, [user, isEffectivePremium]);

  const handleSommelierChange = useCallback(async (level) => {
    if (level === 'expert' && !isEffectivePremium) {
      setComplexityTooltip(true);
      setTimeout(() => setComplexityTooltip(false), 3000);
      return;
    }
    setSommelierLevel(level);
    localStorage.setItem('winesup_sommelier', level);
    try {
      await api.patch('/users/preferences', { sommelier_level: level });
    } catch (err) {
      console.error('[App] Failed to save sommelier preference:', err.message);
    }
  }, [isEffectivePremium]);

  // ─── Feature 4: Audio output ────────────────────────────────────────────────
  const audioSupported = typeof window !== 'undefined' && !!window.speechSynthesis;
  const [audioEnabled, setAudioEnabled] = useState(() => {
    return localStorage.getItem('winesup_audio') === 'true';
  });
  const [speaking, setSpeaking] = useState(false);

  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('winesup_audio', String(next));
      if (!next && window.speechSynthesis) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
      }
      return next;
    });
  }, []);

  const stopSpeech = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, []);

  const speakMessage = useCallback((text) => {
    if (!audioEnabled || !audioSupported) return;
    window.speechSynthesis.cancel();
    const cleanText = stripMarkdown(text);
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [audioEnabled, audioSupported]);

  // ─── Feature 1: scan limit inline error ─────────────────────────────────────
  const [scanLimitInfo, setScanLimitInfo] = useState(null); // { tier, limit, reset_at }

  // ─── Menu scan (OCR) ─────────────────────────────────────────────────────────
  const menuFileRef = useRef(null);
  const [scanLoading, setScanLoading] = useState(false);

  const handleMenuScan = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-selected

    setScanLoading(true);

    // Show a user-side status bubble
    setMessages((prev) => [...prev, {
      id: Date.now(),
      role: 'user',
      content: `📷 Sending you the wine menu from this restaurant…`,
      timestamp: new Date(),
    }]);

    try {
      // Step 1: Extract wines + prices from the menu photo/PDF
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.post('/menus/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!data.success || data.winesFound === 0) {
        setMessages((prev) => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: "I couldn't read any wines from that menu. Try a clearer photo or hold the camera steady.",
          timestamp: new Date(),
        }]);
        return;
      }

      // Step 2: Build a wine-list message and send it to the chat
      // Claude already knows what the user is eating from the conversation history.
      // This injects the actual available wines so Claude recommends from this menu.
      const wineLines = data.wines
        .map((w) => `- ${w.name}${w.price ? ` — ${w.price}` : ''}`)
        .join('\n');

      const langConfig = LANGS.find((l) => l.code === lang) || LANGS[0];
      const langHint = langConfig.hint ? `${langConfig.hint} ` : '';

      const menuMessage = `${langHint}[Wine list from this restaurant]\n${wineLines}\n\nBased on what I'm having, which of these wines would you recommend?`;

      setChatLoading(true);
      const response = await api.post('/chat', {
        conversationId: conversationId || undefined,
        message: menuMessage,
      });

      const assistantContent = response.data.message;
      const recommendedWines = response.data.recommendedWines || [];

      setMessages((prev) => [...prev, {
        id: Date.now() + 2,
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
        recommendedWines,
      }]);

      if (response.data.conversationId && !conversationId) {
        setConversationId(response.data.conversationId);
      }

      speakMessage(assistantContent);

    } catch (err) {
      const errData = err.response?.data;
      let errMsg = 'Sorry, I had trouble scanning that menu. Please try again.';
      if (errData?.error === 'SCAN_LIMIT_REACHED') {
        setScanLimitInfo({ tier: errData.tier, limit: errData.limit, reset_at: errData.reset_at });
        errMsg = null;
      }
      if (errMsg) {
        setMessages((prev) => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: errMsg,
          timestamp: new Date(),
        }]);
      }
    } finally {
      setScanLoading(false);
      setChatLoading(false);
    }
  }, [conversationId, lang, speakMessage]);

  // ─── Feature 6: Feedback state { [wineId]: signal } ─────────────────────────
  const [feedbackMap, setFeedbackMap] = useState({});

  // Seed feedbackMap from API when user logs in
  useEffect(() => {
    if (!user) return;
    api.get('/feedback')
      .then((r) => {
        const map = {};
        (r.data.feedback || []).forEach((f) => { map[f.wineId] = f.signal; });
        setFeedbackMap(map);
      })
      .catch(() => {}); // non-fatal
  }, [user]);

  const handleFeedbackChange = useCallback((wineId, signal) => {
    setFeedbackMap((prev) => {
      if (signal === null) {
        const next = { ...prev };
        delete next[wineId];
        return next;
      }
      return { ...prev, [wineId]: signal };
    });
  }, []);

  // Create a real conversation in the DB when user logs in
  useEffect(() => {
    if (!user) return;
    api.post('/chat/new', {})
      .then((r) => setConversationId(r.data.conversationId))
      .catch((err) => {
        console.error('Failed to create conversation:', err.message);
        setConversationId(null);
      });
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const switchLang = useCallback((code) => {
    setLang(code);
    localStorage.setItem('winesup_lang', code);
  }, []);

  const handleVoiceTranscript = useCallback((text) => {
    setInputValue((prev) => prev ? `${prev} ${text}` : text);
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text) return;

    // Enforce max 8000 char client-side (keep in sync with backend MAX_MESSAGE_CHARS)
    const MAX_CHARS = 8000;
    if (text.length > MAX_CHARS) {
      setMessages((prev) => [...prev, {
        id: prev.length, role: 'assistant',
        content: `Your message is too long (max ${MAX_CHARS} characters). Please shorten it.`,
        timestamp: new Date(),
      }]);
      return;
    }

    // Add user message to UI (show original text — language hint is hidden)
    const userMsg = { id: messages.length, role: 'user', content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setChatLoading(true);

    // Prepend hidden language instruction when not English
    const langConfig = LANGS.find((l) => l.code === lang) || LANGS[0];
    const apiMessage = langConfig.hint ? `${langConfig.hint} ${text}` : text;

    try {
      const response = await api.post('/chat', {
        conversationId: conversationId || undefined,
        message: apiMessage,
      });

      const assistantContent = response.data.message;
      const recommendedWines = response.data.recommendedWines || [];
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length,
          role: 'assistant',
          content: assistantContent,
          timestamp: new Date(),
          recommendedWines,
        },
      ]);

      // Update conversationId if server assigned a new one
      if (response.data.conversationId && !conversationId) {
        setConversationId(response.data.conversationId);
      }

      // Feature 4: Speak the response if audio enabled
      speakMessage(assistantContent);

    } catch (error) {
      const status = error.response?.status;
      let errorContent;
      if (status >= 500) {
        errorContent = 'Having trouble reaching the server, please try again in a moment.';
      } else if (status === 429) {
        errorContent = 'Still having trouble, please try again in a moment.';
      } else {
        errorContent = "I didn't quite catch that — could you rephrase?";
      }
      setMessages((prev) => [
        ...prev,
        { id: prev.length, role: 'assistant', content: errorContent, timestamp: new Date() },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const activeLangConfig = LANGS.find((l) => l.code === lang) || LANGS[0];

  if (loading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="typing-indicator" style={{ padding: '20px' }}>
          <span></span><span></span><span></span>
        </div>
      </div>
    );
  }

  // Age verification gate for non-authenticated visitors
  if (!user && !ageVerified) {
    return <AgeVerificationModal onVerified={() => setAgeVerified(true)} />;
  }

  if (!user) return <LoginPage />;

  // T&C acceptance required after login
  if (!termsAccepted) {
    return (
      <TermsAndConditions onAccepted={() => {
        setTermsAccepted(true);
      }} />
    );
  }

  // Tier badge text
  const tierLabel = (() => {
    if (user.is_tester) return 'TESTER';
    if (effectiveTier === 'premium') return 'PREMIUM';
    if (effectiveTier === 'basic') return 'BASIC';
    return null;
  })();

  return (
    <>
    <div className="App">
      <div className="chat-container">
        <div className="chat-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1>🍷 Wine Sommelier</h1>
              <p>Your AI wine pairing assistant</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              {tierLabel && (
                <span style={{ fontSize: '11px', background: '#8B1A1A', color: '#fff', borderRadius: '12px', padding: '2px 8px', fontWeight: 600 }}>
                  {tierLabel}
                </span>
              )}
              {/* Feature 4: Audio toggle */}
              {audioSupported && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {speaking && (
                    <button
                      onClick={stopSpeech}
                      title="Stop speaking"
                      style={{ fontSize: '11px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: '6px', padding: '2px 6px', cursor: 'pointer' }}
                    >
                      Stop
                    </button>
                  )}
                  <button
                    onClick={toggleAudio}
                    title={audioEnabled ? 'Disable audio output' : 'Enable audio output'}
                    style={{
                      fontSize: '16px', background: audioEnabled ? 'rgba(255,255,255,0.2)' : 'none',
                      border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px',
                      padding: '2px 6px', cursor: 'pointer', color: '#fff',
                    }}
                  >
                    {audioEnabled ? '🔊' : '🔇'}
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px' }}>
                {user.is_admin && (
                  <button
                    onClick={() => setShowAdmin(true)}
                    title="Admin dashboard"
                    style={{ fontSize: '12px', background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer' }}
                  >
                    📊 Admin
                  </button>
                )}
                <button
                  onClick={() => setShowTastePanel(true)}
                  title="Set your taste profile"
                  style={{ fontSize: '12px', background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer' }}
                >
                  🍇 Taste
                </button>
                <button
                  onClick={logout}
                  style={{ fontSize: '12px', background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer' }}
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="chat-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`message message-${msg.role}`}>
              <div className="message-content">
                {renderMarkdown(msg.content)}
              </div>
              <span className="message-time">
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
          {chatLoading && (
            <div className="message message-assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Scan limit inline prompt (Feature 1) */}
        {scanLimitInfo && (
          <div style={{
            background: '#fff8e1', border: '1px solid #f0c040', borderRadius: '8px',
            padding: '10px 14px', margin: '0 16px 8px', fontSize: '0.85rem', color: '#6d4c00',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          }}>
            <span>
              You have reached your {scanLimitInfo.tier} plan limit of {scanLimitInfo.limit} menu scans this month.
              Resets on {scanLimitInfo.reset_at}.
              {!isEffectivePremium && ' Upgrade to Premium for 15 scans/month.'}
            </span>
            <button
              onClick={() => setScanLimitInfo(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#999' }}
            >
              ×
            </button>
          </div>
        )}

        {/* Language selector + complexity selector + chat input */}
        <div className="chat-input-area">
          {/* Controls row: language pills + complexity selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {/* Language pills */}
            <div className="lang-selector" aria-label="Language">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`lang-pill${lang === l.code ? ' active' : ''}`}
                  onClick={() => switchLang(l.code)}
                  aria-pressed={lang === l.code}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* Feature 3: Complexity selector */}
            <div style={{ position: 'relative', display: 'flex', gap: '4px', marginLeft: '8px' }}>
              {[
                { key: 'simple', label: 'Simple', desc: 'Casual and direct. No jargon.' },
                { key: 'balanced', label: 'Balanced', desc: 'Friendly with some wine context.' },
                { key: 'expert', label: 'Expert', desc: 'Full tasting notes, terroir, and pairing rationale.', premiumOnly: true },
              ].map(({ key, label, desc, premiumOnly }) => {
                const isLocked = premiumOnly && !isEffectivePremium;
                const isActive = sommelierLevel === key;
                return (
                  <button
                    key={key}
                    type="button"
                    title={desc}
                    onClick={() => handleSommelierChange(key)}
                    style={{
                      fontSize: '11px',
                      padding: '2px 7px',
                      borderRadius: '10px',
                      border: isActive ? '1.5px solid #722F37' : '1px solid #ccc',
                      background: isActive ? '#722F37' : '#f5f5f5',
                      color: isActive ? '#fff' : (isLocked ? '#aaa' : '#444'),
                      cursor: isLocked ? 'pointer' : 'pointer',
                      fontWeight: isActive ? 600 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                    }}
                  >
                    {isLocked ? '🔒 ' : ''}{label}
                    {isLocked && <span style={{ fontSize: '9px', color: '#e67e22', marginLeft: '2px' }}>Premium</span>}
                  </button>
                );
              })}
              {complexityTooltip && (
                <div style={{
                  position: 'absolute', bottom: '110%', right: 0,
                  background: '#333', color: '#fff', borderRadius: '6px',
                  padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)', zIndex: 100,
                }}>
                  Expert mode requires a Premium subscription.
                  <div style={{ position: 'absolute', bottom: '-5px', right: '20px', width: '10px', height: '10px', background: '#333', transform: 'rotate(45deg)' }} />
                </div>
              )}
            </div>
          </div>

          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <VoiceInput
              onTranscript={handleVoiceTranscript}
              lang={activeLangConfig.speechLang}
              disabled={chatLoading}
            />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Tell me what you're eating or drinking..."
              disabled={chatLoading}
            />
            <input
              ref={menuFileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              style={{ display: 'none' }}
              onChange={handleMenuScan}
            />
            <button
              type="button"
              title="Scan a restaurant menu"
              disabled={scanLoading || chatLoading}
              onClick={() => menuFileRef.current?.click()}
              style={{ fontSize: '18px', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', opacity: scanLoading ? 0.5 : 1 }}
            >
              {scanLoading ? '⏳' : '📷'}
            </button>
            <button type="submit" disabled={chatLoading || !inputValue.trim()}>
              Send
            </button>
          </form>
        </div>
      </div>
    </div>

    {showTastePanel && (
      <TasteProfilePanel onClose={() => setShowTastePanel(false)} />
    )}
    {showAdmin && (
      <AdminDashboard onClose={() => setShowAdmin(false)} />
    )}
  </> );
}

export default App;
